import { useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import {
  Stack, Text, TextInput, Textarea, Select, Button,
  Group, Paper, Title, SimpleGrid, Collapse, Avatar, Anchor, Alert,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconArrowLeft, IconSend, IconCategory, IconAlertTriangle } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { invalidarPendenciasDoChamado } from '@/api/chamadosPendencias'
import { useCurrentTenant, useCan, useAuthStore } from '@/store/auth'
import { CustomFieldsForm } from '@/components/chamados/CustomFieldsForm'
import { EvidenceBuffer, type BufferedFile } from '@/components/chamados'
import { TipoChamadoToggleGroup, type ChamadoTipo, getTipoOption } from '@/components/chamados/TipoChamadoToggleGroup'
import { AtendidoPor, type AtendimentoEscolhido } from '@/components/chamados/AtendidoPor'
import { mensagemDoErroDeCriacao } from './erroDeCriacao'
import { NovoChamadoPreview } from '@/components/chamados/NovoChamadoPreview'
import { runWithConcurrency } from '@/lib/concurrent'

interface FormTemplate {
  id: number
  name: string
  description: string
  category_id: number | null
  default_priority: string
  default_request_type: string
  title_prefix: string | null
  description_template: string | null
  custom_fields_defaults: Record<string, any>
}

interface Category {
  id: number
  name: string
  description: string
  default_priority: string
  default_request_type: string
  sla_first_response_hours: number | null
  sla_resolution_hours: number | null
  custom_fields_schema: Array<{ name: string; label: string; type: 'text' | 'number' | 'select' | 'multiselect' | 'textarea' | 'date'; required?: boolean; options?: string[] }>
  requires_attachments: boolean
}

interface CreatePayload {
  title: string
  description: string
  request_type: string
  priority: string
  attending_org_id?: number
  queue_id?: number
  category_id?: number
  resource_type?: string
  resource_id?: string
  resource_name?: string
  scheduled_start?: string
  scheduled_end?: string
  custom_fields?: Record<string, any>
  attachments_intent?: boolean
  // Proveniência @Atendente: chamado aberto pelo handoff do @Atendente
  // (humano + @Atendente). Vem do draft do handoff (location.state).
  assisted_by_ia?: boolean
}

export function NovoChamado() {
  const navigate = useNavigate()
  const tenantId = useCurrentTenant()?.id
  const queryClient = useQueryClient()
  const can = useCan()
  const canManageCategorias = can('usuarios', 'manage')
  // Handoff do @Atendente pode vir com um tenant CONFIRMADO diferente do tenant
  // ativo. Precisamos do setter do store (mesmo padrão do ChamadoDetalhe cross-tenant)
  // e da lista de tenants do usuário para só trocar se ele tiver acesso.
  const { user, setSelectedTenant, setSelectedOrg } = useAuthStore()
  // Quando o draft aponta um tenant ao qual o usuário NÃO tem acesso, não trocamos
  // silenciosamente nem criamos no tenant errado — exibimos um aviso.
  const [wrongTenantName, setWrongTenantName] = useState<string | null>(null)

  const [title, setTitle] = useState('')
  const [titleError, setTitleError] = useState<string | null>(null)
  const [description, setDescription] = useState('')
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [priority, setPriority] = useState<string>('medium')
  const [requestType, setRequestType] = useState<string>('service_request')
  const [resourceType, setResourceType] = useState<string | null>(null)
  const [resourceId, setResourceId] = useState('')
  const [resourceName, setResourceName] = useState('')
  const [customFields, setCustomFields] = useState<Record<string, any>>({})
  const [files, setFiles] = useState<BufferedFile[]>([])
  // Quem atende: nulo = a cascata decide (categoria, depois padrão da plataforma).
  const [atendimento, setAtendimento] = useState<AtendimentoEscolhido | null>(null)
  const [rotuloAtendimento, setRotuloAtendimento] = useState<string | null>(null)

  // Carregar templates
  const { data: templates = [] } = useQuery<FormTemplate[]>({
    queryKey: ['form-templates', tenantId],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenantId}/form-templates`)
      return Array.isArray(res.data) ? res.data : []
    },
    enabled: !!tenantId,
  })

  const handleTemplateSelect = (templateId: string | null) => {
    if (!templateId) return
    const tpl = templates.find((t) => String(t.id) === templateId)
    if (!tpl) return
    if (tpl.title_prefix) setTitle(tpl.title_prefix + ' ')
    if (tpl.description_template) setDescription(tpl.description_template)
    if (tpl.category_id) handleCategoryChange(String(tpl.category_id))
    setPriority(tpl.default_priority)
    setRequestType(tpl.default_request_type)
    if (tpl.custom_fields_defaults) setCustomFields(tpl.custom_fields_defaults)
  }

  // Carregar categorias
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['ticket-categories', tenantId],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenantId}/ticket-categories`)
      return Array.isArray(res.data) ? res.data : []
    },
    enabled: !!tenantId,
  })

  // Categoria depende do Tipo (#2): lista só as categorias do request_type
  // selecionado. O Tipo é escolhido ANTES da Categoria na tela.
  const categoryOptions = categories
    .filter((c) => (c.default_request_type ?? 'service_request') === requestType)
    .map((c) => ({ value: String(c.id), label: c.name }))

  // Categoria muda → pré-preenche a prioridade (o Tipo já está fixado acima).
  const handleCategoryChange = (val: string | null) => {
    setCategoryId(val)
    if (val) {
      const cat = categories.find((c) => String(c.id) === val)
      if (cat) setPriority(cat.default_priority)
    }
  }

  // Trocar o Tipo limpa a categoria se ela não pertencer ao novo tipo.
  const handleRequestTypeChange = (v: string) => {
    setRequestType(v)
    const cat = categories.find((c) => String(c.id) === categoryId)
    if (cat && (cat.default_request_type ?? 'service_request') !== v) setCategoryId(null)
  }

  // ── Handoff do Atendente: pré-preencher a partir de location.state.draft ──
  // O @Atendente propõe abrir chamado e navega para cá com um rascunho no
  // router state. Esta tela apenas PRÉ-PREENCHE os campos — a criação (e toda
  // a validação PCI + campos obrigatórios) acontece somente ao confirmar aqui.
  const location = useLocation()
  const draft = (location.state as { draft?: Record<string, string> } | null)?.draft
  const draftAppliedRef = useRef(false)
  const draftCategoryRef = useRef(false)
  const draftTenantRef = useRef(false)
  const VALID_PRIORITIES = ['low', 'medium', 'high', 'critical']
  // Narrowing intencional: o Atendente só pré-preenche estes tipos. 'gmud'/'approval'
  // não vêm do handoff do chat (mudança PCI/GMUD é iniciada pelo fluxo próprio) —
  // um request_type fora deste conjunto é ignorado e o usuário escolhe na tela.
  const VALID_REQUEST_TYPES = ['incident', 'service_request', 'change_request']

  useEffect(() => {
    if (!draft || draftAppliedRef.current) return
    draftAppliedRef.current = true
    if (draft.title) setTitle(draft.title)
    if (draft.description) setDescription(draft.description)
    if (draft.priority && VALID_PRIORITIES.includes(draft.priority)) setPriority(draft.priority)
    if (draft.request_type && VALID_REQUEST_TYPES.includes(draft.request_type)) setRequestType(draft.request_type)
    if (draft.resource_type) setResourceType(draft.resource_type)
    if (draft.resource_id) setResourceId(draft.resource_id)
    if (draft.resource_name) setResourceName(draft.resource_name)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  // ── Handoff: tenant CONFIRMADO na triagem ───────────────────────────────────
  // O @Atendente já sabe o tenant real após a triagem guiada. Se o draft trouxer
  // um tenant_id diferente do ativo, trocamos o tenant ativo — MAS só se o usuário
  // tiver acesso a ele (mesmo padrão do ChamadoDetalhe cross-tenant). Se NÃO tiver
  // acesso, NÃO trocamos silenciosamente nem criamos no tenant errado: exibimos um
  // Alert pedindo que ele selecione o tenant correto no topo antes de enviar.
  useEffect(() => {
    if (!draft || draftTenantRef.current) return
    const rawTid = draft.tenant_id
    if (rawTid == null || rawTid === '') return
    const draftTid = Number(rawTid)
    if (!Number.isFinite(draftTid) || draftTid <= 0) return
    draftTenantRef.current = true
    if (draftTid === tenantId) return // já está no tenant certo
    const target = user?.tenants?.find((t) => t.id === draftTid)
    if (target) {
      // Usuário tem acesso → troca determinística do tenant ativo. Zera a org de
      // contexto p/ a reconciliação da Sidebar não reverter o tenant do draft.
      setSelectedOrg(null)
      setSelectedTenant(draftTid)
      notifications.show({
        title: 'Tenant ativo alterado',
        message: `Chamado será aberto no tenant ${target.name}`,
        color: 'blue',
      })
    } else {
      // Sem acesso ao tenant confirmado → não abrir no tenant errado.
      setWrongTenantName(`#${draftTid}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, tenantId, user?.tenants])

  useEffect(() => {
    if (!draft || draftCategoryRef.current || categories.length === 0) return

    // 0) categoria CONFIRMADA por ID (triagem guiada) — pré-seleção determinística.
    //    Preferimos o category_id ao category_hint (palpite por nome). Só casa se a
    //    categoria existir na lista do tenant ativo (evita id órfão de outro tenant).
    const rawCid = draft.category_id
    if (rawCid != null && rawCid !== '') {
      const cid = Number(rawCid)
      const byId = Number.isFinite(cid) ? categories.find((c) => c.id === cid) : undefined
      if (byId) {
        draftCategoryRef.current = true
        // handleCategoryChange: além de setar a categoria, alinha o request_type/
        // priority ao da categoria confirmada (o Tipo filtra as categorias na tela).
        setRequestType(byId.default_request_type ?? 'service_request')
        handleCategoryChange(String(byId.id))
        return
      }
    }

    const hint = (draft.category_hint ?? '').toLowerCase().trim()

    // 1) match por nome (o hint contém o nome da categoria, ou vice-versa) —
    //    ex.: hint "C1" casa "C1 — Regra de Security Group".
    let match = hint
      ? categories.find((c) => {
          const nm = (c.name ?? '').toLowerCase()
          return !!nm && (nm.includes(hint) || hint.includes(nm))
        })
      : undefined

    // 2) fallback: se o hint não casa nenhuma categoria, seleciona a categoria
    //    PADRÃO do tipo do chamado do rascunho (ex.: incidente → "Incidente de
    //    Infraestrutura") — assim a Categoria nunca fica vazia no handoff.
    if (!match) {
      const rt =
        draft.request_type && VALID_REQUEST_TYPES.includes(draft.request_type)
          ? draft.request_type
          : undefined
      if (rt) match = categories.find((c) => c.default_request_type === rt)
    }

    if (match) {
      draftCategoryRef.current = true
      // setCategoryId direto (NÃO handleCategoryChange) para NÃO sobrescrever a
      // priority/request_type que o Atendente já definiu no rascunho.
      setCategoryId(String(match.id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, categories])

  const selectedCategory = categories.find((c) => String(c.id) === categoryId)
  const requiresAttachments = !!selectedCategory?.requires_attachments
  // Sem tenant o POST iria para `/v1/tenants/null/workflow`. Acontece com quem é só
  // de organização sem tenant (consoleai#26145): a saída é o administrador criar o
  // tenant de atendimento da organização, e o aviso abaixo diz isso.
  const semTenant = !tenantId
  const submitDisabled = semTenant || (requiresAttachments && files.length === 0)

  // TASK-2561 MR-E: atalhos de teclado.
  // - Cmd/Ctrl+Enter: submit (se !submitDisabled e nao esta pending).
  // - Esc: cancelar (com confirm se ha dirty state).
  useEffect(() => {
    const isDirty = () => Boolean(
      title.trim() || description.trim() || categoryId || resourceId || resourceName || files.length > 0
    )

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const inFormField = !!target?.closest('input, textarea, [contenteditable="true"]')

      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        if (!submitDisabled && !mutation.isPending) {
          handleSubmitRef.current?.()
        }
        return
      }
      if (e.key === 'Escape' && inFormField) {
        if (isDirty() && !window.confirm('Descartar este chamado? Há campos preenchidos.')) {
          return
        }
        e.preventDefault()
        navigate('/chamados')
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitDisabled, title, description, categoryId, resourceId, resourceName, files.length])

  // Ref pra evitar stale closure do handleSubmit no useEffect.
  const handleSubmitRef = useRef<null | (() => void)>(null)

  function updateFileAt(index: number, patch: Partial<BufferedFile>) {
    setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)))
  }

  async function uploadOne(
    ticketId: number,
    bf: BufferedFile,
    index: number,
  ): Promise<void> {
    const basePath = `/v1/tenants/${tenantId}/workflow/${ticketId}/attachments`
    const mime = bf.file.type || 'application/octet-stream'

    updateFileAt(index, { status: 'uploading', progress: 5 })

    // 1. presign
    const presign = await api.post<{ presigned_put_url: string; s3_key: string }>(
      `${basePath}/presign`,
      { filename: bf.file.name, mime_type: mime, size_bytes: bf.file.size },
    )
    const { presigned_put_url, s3_key } = presign.data

    // 2. upload via XHR (para ter progress)
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('PUT', presigned_put_url)
      xhr.setRequestHeader('Content-Type', mime)
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.min(95, Math.round((e.loaded / e.total) * 90) + 5)
          updateFileAt(index, { progress: pct })
        }
      }
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`PUT ${xhr.status}`))
      xhr.onerror = () => reject(new Error('network error'))
      xhr.send(bf.file)
    })

    // 3. confirm
    await api.post(`${basePath}`, {
      s3_key,
      filename: bf.file.name,
      mime_type: mime,
      size_bytes: bf.file.size,
    })

    updateFileAt(index, { status: 'done', progress: 100 })
  }

  const mutation = useMutation({
    mutationFn: async (payload: CreatePayload) => {
      // 1. criar ticket (com attachments_intent se houver arquivos)
      const created = await api.post<{ id: number }>(
        `/v1/tenants/${tenantId}/workflow`,
        { ...payload, attachments_intent: files.length > 0 },
      )
      const ticketId = created.data.id

      // 2. uploads paralelos (limite 3) se houver arquivos
      const failed: Array<{ name: string; error: string }> = []
      if (files.length > 0) {
        const indexed = files.map((bf, i) => ({ bf, i }))
        await runWithConcurrency(
          indexed,
          3,
          async ({ bf, i }) => {
            try {
              await uploadOne(ticketId, bf, i)
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err)
              updateFileAt(i, { status: 'failed', error: msg })
              failed.push({ name: bf.file.name, error: msg })
            }
          },
          { failFast: false },
        )
      }

      return { ticketId, failed }
    },
    onSuccess: ({ ticketId, failed }) => {
      invalidarPendenciasDoChamado(queryClient)  // criar entra na lista, no quadro e no menu
      if (failed.length > 0) {
        notifications.show({
          color: 'yellow',
          title: 'Alguns anexos falharam',
          message: failed.map((f) => `${f.name}: ${f.error}`).join('; '),
        })
      } else {
        notifications.show({ color: 'green', message: 'Chamado criado com sucesso!' })
      }
      navigate(`/chamados/${ticketId}`)
    },
    onError: (err: Error & { response?: { data?: { detail?: unknown } } }) => {
      // 422 de atendimento, campos obrigatórios da categoria...: a frase, não o código HTTP.
      notifications.show({ color: 'red', message: mensagemDoErroDeCriacao(err) })
    },
  })

  const handleSubmit = () => {
    if (!title.trim()) {
      setTitleError('O título e obrigatório.')
      return
    }
    setTitleError(null)
    const payload: CreatePayload = {
      title: title.trim(),
      description: description.trim(),
      request_type: requestType,
      priority,
      attending_org_id: atendimento?.attending_org_id,
      queue_id: atendimento?.queue_id,
      category_id: categoryId ? Number(categoryId) : undefined,
      resource_type: resourceType ?? undefined,
      resource_id: resourceId.trim() || undefined,
      resource_name: resourceName.trim() || undefined,
      custom_fields: Object.keys(customFields).length > 0 ? customFields : undefined,
      // Proveniência: o handoff do @Atendente marca o draft; o chamado registra
      // que foi criado pelo humano + @Atendente (origin='ia_assistant').
      assisted_by_ia: draft?.assisted_by_ia ? true : undefined,
    }
    mutation.mutate(payload)
  }

  const needsResource = ['incident', 'change_request', 'gmud'].includes(requestType)
  const tipoOption = getTipoOption(requestType)
  const currentTenant = useCurrentTenant()

  // MR-E: mantem ref atualizado em todo render p/ useEffect de atalhos
  // capturar a versao mais recente do handleSubmit sem stale closure.
  handleSubmitRef.current = handleSubmit

  return (
    <Stack gap="lg">
      {/* Header full-width acima do grid editorial */}
      <Group>
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/chamados')}
        >
          Voltar
        </Button>
        <Title order={3}>Novo Chamado</Title>
      </Group>

      {wrongTenantName && (
        <Alert
          color="orange"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          title="Tenant do chamado diferente do ativo"
        >
          {`Este chamado deve ser criado no tenant ${wrongTenantName}, ao qual você não tem acesso no momento. Selecione-o no topo antes de enviar — não crie no tenant errado.`}
        </Alert>
      )}

      {semTenant && (
        <Alert
          color="orange"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          title="Você ainda não tem onde abrir chamado"
        >
          Todo chamado é aberto num tenant, e a sua organização ainda não tem nenhum. Peça ao
          administrador do ConsoleAI para criar o tenant de atendimento da organização, em
          Administração → Organizações.
        </Alert>
      )}

      {templates.length > 0 && (
        <Paper shadow="xs" p="md" withBorder>
          <Select
            label="Usar template"
            placeholder="Selecionar template para preencher automaticamente"
            clearable
            searchable
            nothingFoundMessage="Nenhum template encontrado"
            data={templates.map((t) => ({ value: String(t.id), label: t.name }))}
            onChange={handleTemplateSelect}
          />
        </Paper>
      )}

      {/* === LAYOUT EDITORIAL: 2/3 conteudo + 1/3 metadata ===
          Pattern do ChamadoDetalhe (cols={{ base: 1, lg: 3 }} + gridColumn
          span 2). Body inteiro do app, em vez de form centrado a 720px.
          Em mobile (lg breakpoint cai), volta a single column natural.   */}
      <SimpleGrid cols={{ base: 1, lg: 3 }} spacing="lg">
        {/* === COLUNA PRINCIPAL: CONTEUDO (titulo + descricao) ===
            Spans 2/3 da largura em desktop. Heading do documento.
            MR-B: borda esquerda com cor tematica do tipo selecionado. */}
        <Paper
          shadow="xs"
          p="xl"
          withBorder
          style={{
            gridColumn: 'span 2',
            borderLeftWidth: 4,
            borderLeftStyle: 'solid',
            borderLeftColor: `var(--mantine-color-${tipoOption?.color ?? 'gray'}-6)`,
            transition: 'border-left-color 180ms ease',
          }}
        >
          <Stack gap="lg">
            <Stack gap={6}>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: 0.5 }}>
                Título <Text component="span" c="red.6">*</Text>
              </Text>
              <TextInput
                placeholder="O que precisa ser feito? (uma frase clara)"
                required
                value={title}
                onChange={(e) => { setTitle(e.target.value); if (titleError) setTitleError(null) }}
                error={titleError}
                size="md"
                styles={{
                  input: { fontSize: 20, fontWeight: 500, lineHeight: 1.3, paddingBlock: 12 },
                }}
                aria-label="Título do chamado"
              />
            </Stack>

            <Stack gap={6}>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: 0.5 }}>
                Descrição
              </Text>
              <Textarea
                placeholder="Descreva em detalhes o que está ocorrendo, o que é necessário, ou cole logs/erros aqui."
                autosize
                minRows={10}
                maxRows={30}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                styles={{ input: { fontSize: 14, lineHeight: 1.55 } }}
                aria-label="Descrição do chamado"
              />
            </Stack>
          </Stack>
        </Paper>

        {/* === COLUNA SECUNDARIA: METADATA (sidebar editorial) ===
            Span 1/3 em desktop. Empilha vertical: Classificacao,
            Evidencias, Para quem? — campos auxiliares mas necessarios.  */}
        <Stack gap="md">
          {/* Classificacao */}
          <Paper shadow="xs" p="md" withBorder>
            <Stack gap="sm">
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: 0.5 }}>
                Classificação
              </Text>
              <Stack gap="sm">
                <div>
                  <Text size="sm" fw={500} mb={4}>Tipo de Chamado</Text>
                  <TipoChamadoToggleGroup
                    value={requestType as ChamadoTipo}
                    onChange={handleRequestTypeChange}
                  />
                </div>
                {categories.length === 0 ? (
                  /* TASK-2560 MR-D: empty state com CTA. Pattern visual identico
                     ao TeamSelectCard empty state (Avatar + title + desc + anchor).
                     Link interno se user pode gerenciar; mailto pra demais. */
                  <Stack gap="xs" align="center" py="sm">
                    <Avatar radius="xl" size={36} color="gray">
                      <IconCategory size={18} />
                    </Avatar>
                    <Text size="sm" fw={500} ta="center">Sem categorias neste tenant</Text>
                    <Text size="xs" c="dimmed" ta="center" maw={260}>
                      Você pode criar o chamado sem categoria — o time receberá com prioridade Média por padrão.
                    </Text>
                    {canManageCategorias ? (
                      <Anchor
                        size="xs"
                        onClick={(e) => { e.preventDefault(); navigate('/admin/chamados/categorias') }}
                        style={{ cursor: 'pointer' }}
                      >
                        Cadastrar categoria →
                      </Anchor>
                    ) : (
                      <Anchor
                        size="xs"
                        href="mailto:devops@amazonasinovare.com.br?subject=Cadastro de categoria de chamado"
                      >
                        Falar com admin →
                      </Anchor>
                    )}
                  </Stack>
                ) : (
                  <Select
                    label="Categoria"
                    placeholder="Selecionar"
                    clearable
                    searchable
                    nothingFoundMessage="Nenhuma categoria cadastrada"
                    data={categoryOptions}
                    value={categoryId}
                    onChange={handleCategoryChange}
                    size="sm"
                  />
                )}
                {draft?.category_hint && !categoryId && (
                  <Text size="xs" c="dimmed">Sugestão do Atendente: {draft.category_hint}</Text>
                )}
                <Select
                  label="Prioridade"
                  value={priority}
                  onChange={(v) => setPriority(v ?? 'medium')}
                  data={[
                    { value: 'low',      label: 'Baixa' },
                    { value: 'medium',   label: 'Média' },
                    { value: 'high',     label: 'Alta' },
                    { value: 'critical', label: 'Crítica' },
                  ]}
                  size="sm"
                />
              </Stack>

              {/* Campos personalizados da categoria — herdam contexto de metadata */}
              {categoryId && categories.find((c) => String(c.id) === categoryId)?.custom_fields_schema?.length ? (
                <CustomFieldsForm
                  schema={categories.find((c) => String(c.id) === categoryId)!.custom_fields_schema}
                  values={customFields}
                  onChange={setCustomFields}
                />
              ) : null}

              {/* Recurso afetado — so quando tipo o requer.
                  MR-E: slide-down via Mantine Collapse com transitionDuration. */}
              <Collapse in={needsResource} transitionDuration={220}>
                <Stack gap={6} mt="xs">
                  <Text size="xs" c="dimmed" fw={500}>Recurso afetado (opcional)</Text>
                  <Stack gap="xs">
                    <Select
                      label="Tipo"
                      placeholder="Tipo de recurso"
                      clearable
                      value={resourceType}
                      onChange={setResourceType}
                      data={[
                        { value: 'ec2',       label: 'EC2 (Instância)' },
                        { value: 'elb',       label: 'Load Balancer' },
                        { value: 's3',        label: 'S3 Bucket' },
                        { value: 'domain',    label: 'Domínio / DNS' },
                        { value: 'container', label: 'Container Docker' },
                        { value: 'secret',    label: 'Secret' },
                        { value: 'other',     label: 'Outro' },
                      ]}
                      size="xs"
                    />
                    <TextInput
                      label="ID do recurso"
                      placeholder="ex: i-0abc123"
                      value={resourceId}
                      onChange={(e) => setResourceId(e.target.value)}
                      size="xs"
                    />
                    <TextInput
                      label="Nome amigável"
                      placeholder="ex: ec2-web-01"
                      value={resourceName}
                      onChange={(e) => setResourceName(e.target.value)}
                      size="xs"
                    />
                  </Stack>
                </Stack>
              </Collapse>
            </Stack>
          </Paper>

          {/* Evidencias */}
          <Paper shadow="xs" p="md" withBorder>
            <Stack gap="sm">
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: 0.5 }}>
                Evidências {requiresAttachments && <Text component="span" c="red.6">*</Text>}
              </Text>
              <EvidenceBuffer
                files={files}
                onChange={setFiles}
                required={requiresAttachments}
                disabled={mutation.isPending}
              />
            </Stack>
          </Paper>

          {/* Quem atende (Organização · fila) — spec 2026-10-02 §8 */}
          <Paper shadow="xs" p="md" withBorder>
            <Stack gap="sm">
              <Group justify="space-between" align="baseline" wrap="wrap">
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: 0.5 }}>
                  Para quem?
                </Text>
                <Text size="xs" c="dimmed">Quem vai cuidar</Text>
              </Group>
              <AtendidoPor
                tenantId={tenantId}
                categoryId={categoryId}
                value={atendimento}
                onChange={setAtendimento}
                onRotulo={setRotuloAtendimento}
              />
            </Stack>
          </Paper>
        </Stack>
      </SimpleGrid>

      {/* === LIVE PREVIEW FOOTER (MR-D) — atualiza em tempo real === */}
      <NovoChamadoPreview
        title={title}
        tipoOption={tipoOption}
        priority={priority}
        tenantName={currentTenant?.name ?? null}
        teamName={rotuloAtendimento}
      />

      {/* === ACOES — full width abaixo do grid === */}
      <Group justify="space-between" align="center">
        <Text size="xs" c="dimmed">
          {submitDisabled && requiresAttachments && files.length === 0 && '⚠ Esta categoria exige pelo menos uma evidência'}
        </Text>
        <Group>
          <Button
            variant="default"
            onClick={() => navigate('/chamados')}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            leftSection={<IconSend size={14} />}
            onClick={handleSubmit}
            loading={mutation.isPending}
            disabled={submitDisabled}
            color={tipoOption?.color}
          >
            Criar Chamado
          </Button>
        </Group>
      </Group>
    </Stack>
  )
}
